import * as THREE from "three";
import { twoline2satrec, propagate, gstime, eciToGeodetic, degreesLat, degreesLong } from "satellite.js";

const EARTH_RADIUS_KM = 6371;

// SGP4 drifts by kilometres a day and the ISS reboosts every few weeks, so
// away from the elements' epoch the position is fiction; past this, hide it.
const MAX_EPOCH_DISTANCE_MS = 3 * 86_400_000;

// The drawn track: the orbit around the station, behind and ahead of it.
const TRACK_BEHIND_MS = 45 * 60_000;
const TRACK_AHEAD_MS = 45 * 60_000;
const TRACK_STEP_MS = 30_000;

/** Parses a 3-line (or 2-line) TLE into a satrec, or null if it doesn't parse. */
export function parseTle(text) {
  const lines = text.split("\n").map((l) => l.trim()).filter(Boolean);
  const l1 = lines.find((l) => l.startsWith("1 "));
  const l2 = lines.find((l) => l.startsWith("2 "));
  if (!l1 || !l2) return null;
  const satrec = twoline2satrec(l1, l2);
  return satrec.error ? null : satrec;
}

// The ISS's current elements (NORAD 25544) from the backend's /iss_tle, which
// shares one copy of CelesTrak's among all visitors (CelesTrak blocks
// addresses that re-download unchanged data).
export function fetchIssTle(backendUrl) {
  return fetch(`${backendUrl}/iss_tle`)
    .then((r) => (r.ok ? r.text() : Promise.reject(new Error(`TLE ${r.status}`))))
    .then((text) => parseTle(text) || Promise.reject(new Error("Unparseable TLE")));
}

const epochMs = (satrec) => (satrec.jdsatepoch - 2440587.5) * 86_400_000;

/** Whether the elements are close enough to `date` to trust. */
export function trackable(satrec, date) {
  return Math.abs(date.getTime() - epochMs(satrec)) <= MAX_EPOCH_DISTANCE_MS;
}

/**
 * The station's geographic position at `date`: { lat, lng } in degrees and
 * altitude as a fraction of Earth's radius (globe.getCoords' units), or null.
 */
export function issPosition(satrec, date) {
  const { position } = propagate(satrec, date);
  if (!position) return null;
  const geo = eciToGeodetic(position, gstime(date));
  return {
    lat: degreesLat(geo.latitude),
    lng: degreesLong(geo.longitude),
    altitude: geo.height / EARTH_RADIUS_KM,
  };
}

// The model's span in Earth radii (~640 km), fixed so it zooms with the
// globe: at true scale (109 m) it would be far below a pixel.
const MODEL_SIZE = 0.1;
// Light left on it in Earth's shadow, so it doesn't vanish outright.
const ECLIPSE_LIGHT = 0.12;

/**
 * A simple ISS in its own frame, one unit across: X is the direction of
 * flight, Y points away from Earth, Z runs along the main truss. Returns the
 * model and the solar wings, which turn about the truss to face the Sun.
 */
function buildIssModel() {
  const model = new THREE.Group();
  const metal = new THREE.MeshStandardMaterial({ color: 0xd8d8d0, roughness: 0.55, metalness: 0.4 });
  const hull = new THREE.MeshStandardMaterial({ color: 0xf2f2ec, roughness: 0.8, metalness: 0.1 });
  const array = new THREE.MeshStandardMaterial({ color: 0xb07a2a, roughness: 0.35, metalness: 0.6, side: THREE.DoubleSide });
  const radiator = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.9, metalness: 0.0, side: THREE.DoubleSide });
  const materials = [metal, hull, array, radiator];

  const box = (w, h, d, mat) => new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);
  const module = (length, radius) => {
    const m = new THREE.Mesh(new THREE.CylinderGeometry(radius, radius, length, 16), hull);
    m.rotation.z = Math.PI / 2; // cylinders stand on Y; lay them along X
    return m;
  };

  // Main truss.
  model.add(box(0.03, 0.03, 1.0, metal));

  // Pressurised modules, fore and aft of the truss, with a cross module.
  const fwd = module(0.34, 0.028); fwd.position.set(0.19, -0.035, 0); model.add(fwd);
  const aft = module(0.3, 0.03); aft.position.set(-0.17, -0.035, 0); model.add(aft);
  const node = new THREE.Mesh(new THREE.CylinderGeometry(0.024, 0.024, 0.2, 16), hull);
  node.rotation.x = Math.PI / 2; node.position.set(0.3, -0.035, 0); model.add(node);
  const service = module(0.08, 0.036); service.position.set(-0.36, -0.035, 0); model.add(service);

  // Radiators hang below the truss either side of the modules.
  for (const z of [-0.14, 0.14]) {
    const r = box(0.001, 0.12, 0.07, radiator);
    r.position.set(0, -0.08, z);
    model.add(r);
  }

  // Four solar wing pairs, two at each end of the truss; each wing runs
  // fore or aft from a mast on the truss.
  const wings = [];
  for (const z of [-0.44, -0.34, 0.34, 0.44]) {
    const pivot = new THREE.Group();
    pivot.position.set(0, 0, z);
    for (const side of [-1, 1]) {
      const wing = box(0.32, 0.001, 0.075, array);
      wing.position.x = side * 0.18;
      pivot.add(wing);
    }
    pivot.add(box(0.36, 0.006, 0.006, metal)); // mast
    model.add(pivot);
    wings.push(pivot);
  }

  const baseColours = materials.map((m) => m.color.clone());
  return { model, wings, materials, baseColours };
}

/**
 * The ISS as a small model flying along its orbit (nose forward, belly to
 * Earth, wings on the Sun), plus the orbit as a line fading out behind it.
 * The model has a fixed (exaggerated) size on the globe; Earth occludes
 * both through the depth buffer.
 *
 * Returns:
 *   setElements(satrec) — new TLE
 *   update(date, sunDir) — place the model (cheap; call every frame)
 *   setVisible(on)
 *   dispose()
 */
export function createIssMarker(globe) {
  const scene = globe.scene();
  const group = new THREE.Group();
  group.visible = false;
  scene.add(group);

  const { model, wings, materials, baseColours } = buildIssModel();
  group.add(model);

  const count = Math.floor((TRACK_BEHIND_MS + TRACK_AHEAD_MS) / TRACK_STEP_MS) + 1;
  const trackGeometry = new THREE.BufferGeometry();
  trackGeometry.setAttribute("position", new THREE.BufferAttribute(new Float32Array(count * 3), 3));
  const colours = new Float32Array(count * 4);
  for (let i = 0; i < count; i++) {
    const t = i * TRACK_STEP_MS - TRACK_BEHIND_MS;
    // Solid where it's been, faint where it's going.
    const alpha = t <= 0 ? 0.7 * (1 + t / TRACK_BEHIND_MS) : 0.25 * (1 - t / TRACK_AHEAD_MS);
    colours.set([0.55, 0.8, 1.0, alpha], i * 4);
  }
  trackGeometry.setAttribute("color", new THREE.BufferAttribute(colours, 4));
  const track = new THREE.Line(
    trackGeometry,
    new THREE.LineBasicMaterial({ vertexColors: true, transparent: true, depthWrite: false, toneMapped: false })
  );
  track.renderOrder = 3;
  track.frustumCulled = false;
  group.add(track);

  let satrec = null;
  let shown = false;
  let trackCentre = null; // the moment the track was last drawn around

  const drawTrack = (date) => {
    const positions = trackGeometry.attributes.position;
    let last = null;
    for (let i = 0; i < count; i++) {
      const p = issPosition(satrec, new Date(date.getTime() + i * TRACK_STEP_MS - TRACK_BEHIND_MS));
      const v = p ? globe.getCoords(p.lat, p.lng, p.altitude) : last;
      if (v) positions.setXYZ(i, v.x, v.y, v.z);
      last = v || last;
    }
    positions.needsUpdate = true;
    trackGeometry.computeBoundingSphere();
    trackCentre = date.getTime();
  };

  const ahead = new THREE.Vector3();
  const forward = new THREE.Vector3();
  const up = new THREE.Vector3();
  const side = new THREE.Vector3();
  const basis = new THREE.Matrix4();
  const localSun = new THREE.Vector3();
  const alongSun = new THREE.Vector3();

  model.scale.setScalar(globe.getGlobeRadius() * MODEL_SIZE);

  const update = (date, sunDir) => {
    const on = shown && satrec != null && trackable(satrec, date);
    const p = on ? issPosition(satrec, date) : null;
    const next = p && issPosition(satrec, new Date(date.getTime() + 1000));
    group.visible = next != null;
    if (!next) return;
    model.position.copy(globe.getCoords(p.lat, p.lng, p.altitude));
    ahead.copy(globe.getCoords(next.lat, next.lng, next.altitude));

    // Fly level: nose along the ground-relative velocity, belly to Earth.
    up.copy(model.position).normalize();
    forward.subVectors(ahead, model.position);
    forward.addScaledVector(up, -forward.dot(up)).normalize();
    side.crossVectors(forward, up);
    basis.makeBasis(forward, up, side);
    model.quaternion.setFromRotationMatrix(basis);

    if (sunDir) {
      // Wings turn about the truss (local Z) to face the Sun.
      localSun.copy(sunDir).applyQuaternion(model.quaternion.clone().invert());
      const angle = Math.atan2(-localSun.x, localSun.y);
      for (const w of wings) w.rotation.z = angle;

      // Dark in Earth's shadow: behind the planet and within its radius of the axis.
      const along = model.position.dot(sunDir);
      alongSun.copy(sunDir).multiplyScalar(along);
      const eclipsed = along < 0 && model.position.distanceTo(alongSun) < globe.getGlobeRadius();
      const light = eclipsed ? ECLIPSE_LIGHT : 1;
      materials.forEach((m, i) => m.color.copy(baseColours[i]).multiplyScalar(light));
    }

    // The track is ~180 propagations; redraw it only when the station
    // has moved along it noticeably (or the timeline jumped).
    if (trackCentre == null || Math.abs(date.getTime() - trackCentre) > 20_000) drawTrack(date);
  };

  return {
    group,
    setElements(next) {
      satrec = next;
      trackCentre = null;
    },
    update,
    setVisible(on) {
      shown = on;
      if (!on) group.visible = false;
    },
    dispose() {
      scene.remove(group);
      model.traverse((o) => o.geometry?.dispose());
      materials.forEach((m) => m.dispose());
      trackGeometry.dispose();
      track.material.dispose();
    },
  };
}
